import React, {createContext, useContext, useMemo} from "react";
import {BizFormApiClient, createBizFormApiClient} from "../../core/services/api";
import {BizFormApiTypes} from "../../core/services/api/biz-form-api-client";

const BizFormApiReactContext = createContext<BizFormApiClient | null>(null);

export interface BizFormApiProviderProps {
    type?: BizFormApiTypes;
    /** BizForm API root URL (e.g. from env or module config). Ignored when `client` is set. */
    baseUrl?: string;
    /** Pre-built client; wins over `baseUrl`. */
    client?: BizFormApiClient;
    children: React.ReactNode;
}

/**
 * Scopes BizForm HTTP calls to a specific API base URL for this subtree.
 */
export const BizFormApiProvider: React.FunctionComponent<BizFormApiProviderProps> = ({type, baseUrl, client, children}) => {
    const value = useMemo(() => {
        if (client) {
            return client;
        }
        if (baseUrl != null && baseUrl.trim() !== "") {
            return createBizFormApiClient(type ?? "bizForm", baseUrl);
        }
        return createBizFormApiClient();
    }, [client, baseUrl, type]);

    return <BizFormApiReactContext.Provider value={value}>{children}</BizFormApiReactContext.Provider>;
};

/**
 * Returns the BizForm API client from the nearest provider, or the default env-based client.
 */
export function useBizFormApi(): BizFormApiClient {
    return useContext(BizFormApiReactContext) ?? createBizFormApiClient();
}


export interface WithBizFormApiProviderOptions {
    apiType?: BizFormApiTypes;
    /** Optional API base URL to use for this subtree (e.g. from env or module config). Ignored when `apiClient` is set. */
    apiBaseUrl?: string;
    /** Optional pre-built client to use for this subtree; wins over `apiBaseUrl`. */
    apiClient?: BizFormApiClient;
}